import { Select, Table, TableColumnsType } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import QueryString from 'qs';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  getAttendances,
  sendAttendanceMessages,
  updateAttendance,
} from '../../../api/tutor.api';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import { IClass } from '../../../models/campusClasses.model';
import { ITimetable } from '../../../models/campusClasses3.model';
import {
  IAttendance,
  IGetAttendances,
  IPostAttendance,
} from '../../../models/tutor.model';
import { RootState } from '../../../store/rootReducer';
import Button from '../../Button';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Loader';
import DateTable from './DateTable';
import { setStudentTabIndex } from '../../../store/class.slice';

interface IProps {
  classData: IClass | undefined;
  timetables: ITimetable[] | undefined;
}

const Attendance: FC<IProps> = ({ classData, timetables }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // CL0001 = 출결 상태
  const CL0001 = commonCodes.find(
    (item) => item.commonCode === 'CL0001',
  )?.detailCode;
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetAttendances>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios<IPostAttendance>();
  const {
    data: submitRes2,
    isLoading: submitIsLoading2,
    error: submitError2,
    execute: submitExecute2,
  } = useAxios<any>();

  const [classCode, setClassCode] = useState<string>();
  const [timetableCode, setTimetableCode] = useState<number>();
  const [data, setData] = useState<IAttendance[]>();

  useEffect(() => {
    if (query.classCode && query.timetableCode) {
      const _classCode = String(query.classCode);
      const _timetableCode = Number(query.timetableCode);
      setClassCode(_classCode);
      setTimetableCode(_timetableCode);
      execute(getAttendances(_classCode, _timetableCode));
    }
  }, [location]);

  useEffect(() => {
    if (res && !isLoading) {
      setData(_.cloneDeep(res.data)?.sort((a, b) =>
        a.studentKorName < b.studentKorName ? -1 :
          a.studentKorName > b.studentKorName ? 1 : 0));
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      const _data = _.cloneDeep(data);
      const student = _data?.find(
        (item) => item.memberCode === submitRes.data.memberCode,
      );
      if (student) {
        student.attendCode = submitRes.data.attendCode;
      }
      setData(_data);
    }
  }, [submitRes, submitIsLoading]);

  useEffect(() => {
    if (submitRes2 && !submitIsLoading2) {
      successNotification(t('common.message.submit'));
      const _data = _.cloneDeep(data);
      submitRes2.data.forEach((item: any) => {
        const student = _data?.find(
          (subitem) => subitem.memberCode === item.memberCode,
        );
        if (student) {
          student.smsSendDatetime = item.smsSendDatetime;
        }
      });
      setData(_data);
    }
  }, [submitRes2, submitIsLoading2]);

  const onSetAttendStatus = (index: number, value: string) => {
    if (!data) return;

    const _data = [...data];
    _data[index].attendStatus = value;
    setData(_data);
    submitAttendance(_data[index]);
  };

  const submitAttendance = (req: any) => {
    if (!classCode || !timetableCode) return;
    req.studentCode = req.memberCode;
    submitExecute(updateAttendance(classCode, timetableCode, req));
  };

  const onSetMemo = (index: number, value: string) => {
    if (!data) return;

    const _data = [...data];
    _data[index].memo = value;
    setData(_data);
  };

  const onSendMessages = () => {
    if (!data) return;

    const req: any = [];
    data.forEach((item) => {
      if (item.attendCode) {
        req.push({ attendCode: item.attendCode });
      }
    });

    submitExecute2(sendAttendanceMessages(req));
  };

  const onSendMessage = (record: any) => {
    if (!record.attendCode) return;
    submitExecute2(sendAttendanceMessages([{ attendCode: record.attendCode }]));
  };

  const columns: TableColumnsType<IAttendance> = [
    {
      title: t('class.tutor.column7'),
      dataIndex: 'studentKorName',
      key: 'studentKorName',
      align: 'center',
      //20240507 대시보드 > 학생 클릭시 > 학생관리 화면으로 이동
      render: (value, record) => (
        <a
          onClick={() => {
            history.push({
              pathname: '/student/student',
              search: `?campusCode=${classData?.campusCode}&studentName=${record.studentEngName}&page=1&field=studentName&keyword=${record.studentEngName}&memberCode=${record.memberCode}`,
              state: dispatch(setStudentTabIndex(true)),
            });
          }}
        >
          {value} ({record.studentEngName})
        </a>
      ),
    },
    {
      title: t('common.column.status'),
      dataIndex: 'attendStatus',
      key: 'attendStatus',
      align: 'center',
      render: (value, record, index) => (
        <Select
          className="w-100p"
          placeholder={t('common.button.select')}
          value={value}
          onChange={(value) => onSetAttendStatus(index, value)}
          disabled={value === 'NT'}
        >
          {CL0001?.map((item: any) => {
            return (
              <Select.Option
                key={item.detailCode}
                value={item.detailCode}
                disabled={item.detailCode === 'NT'}
              >
                {i18n.language === 'ko' ? item.korName : item.engName}
              </Select.Option>
            );
          })}
        </Select>
      ),
    },
    {
      title: t('class.tutor.column8'),
      dataIndex: 'memo',
      key: 'memo',
      align: 'center',
      render: (value, record, index) => (
        <input
          type="text"
          className="form-control"
          value={value || ''}
          onChange={(e) => onSetMemo(index, e.target.value)}
          onBlur={() => submitAttendance(record)}
        />
      ),
    },
    {
      title: t('class.tutor.column9'),
      dataIndex: 'smsSendDatetime',
      key: 'smsSendDatetime',
      align: 'center',
      render: (value) =>
        value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '',
    },
    {
      title: t('class.tutor.column10'),
      dataIndex: 'attendCode',
      key: 'attendCode',
      align: 'center',
      render: (value, record) => (
        <Button
          className="btn btn-info btn-w55p"
          onClick={() => onSendMessage(record)}
          text={t('class.tutor.button2')}
          disabled={!value}
        />
      ),
    },
  ];

  return (
    <>
      {isLoading || submitIsLoading || submitIsLoading2 ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />
      <ErrorResponse error={submitError2} />

      <DateTable classData={classData} timetables={timetables} />

      <div className="mt-4 comm-btn__area right">
        <Button
          className="btn-basic pink"
          onClick={onSendMessages}
          text={t('class.tutor.button3')}
        />
      </div>

      <div>
        <Table
          className='comm-table__area mt-2'
          columns={columns}
          dataSource={data}
          pagination={{ pageSize: 9999999 }}
          rowClassName="pointer"
        />
      </div>
    </>
  );
};

export default Attendance;
